import type {Instant, UUID} from './common'
import type {AutoAssignRuleType} from './auto-assign-rule'
import type {InitialOwnerRequest} from './owner'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

/**
 * Auto-assign rule request types for frontend.
 */

export const RuleRequestType = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    ACTIVATE: 'ACTIVATE',
    DEACTIVATE: 'DEACTIVATE',
} as const;
export type RuleRequestType = typeof RuleRequestType[keyof typeof RuleRequestType];

export const RuleRequestStatus = {
    DRAFT: 'DRAFT',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
} as const;
export type RuleRequestStatus = typeof RuleRequestStatus[keyof typeof RuleRequestStatus];

/**
 * Owner party summary embedded in responses.
 */
export interface PartyRef {
    id: UUID;
    displayName: string;
}

/**
 * Creator/reviewer identity summary.
 */
export interface IdentityRef {
    id: UUID;
    partyDisplayName: string;
}

import type {ApprovalStep} from '@/types/access-request'

/**
 * Auto-assign rule request response from the API.
 */
export interface RuleRequestOwnerResponse {
    ownerPartyId: UUID;
    ownerPartyDisplayName: string;
    responsibility: string;
}

export interface RuleRequest {
    id: UUID;
    requestType: RuleRequestType;
    status: RuleRequestStatus;
    ruleId: UUID | null;
    ruleType: AutoAssignRuleType;
    name: string;
    description: string | null;
    conditionExpr: string;
    priority: number;
    removalGraceDays: number;
    owners: RuleRequestOwnerResponse[];
    userRisk: number | null;
    profileIds: UUID[];
    functionGroupIds: UUID[];
    reviewedByIdentityId: UUID | null;
    reviewedByDisplayName: string | null;
    reviewComment: string | null;
    reviewedAt: Instant | null;
    matchedPolicyName: string | null;
    approvalChain: ApprovalStep[];
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

/**
 * Request to create a new auto-assign rule request.
 * Mirrors backend record AutoAssignRuleRequestCreateRequest.
 */
/**
 * Mutable rule body shared by the create and update request DTOs — create adds
 * {@link AutoAssignRuleRequestCreateRequest.requestType}/`ruleId`, update adds
 * `version`. Extracted so the two variants don't duplicate the field block.
 */
export interface AutoAssignRuleRequestBody {
    ruleType?: AutoAssignRuleType;
    name: string;
    description?: string;
    conditionExpr: string;
    priority: number;
    removalGraceDays: number;
    owners: InitialOwnerRequest[];
    userRisk?: number;
    profileIds?: UUID[];
    functionGroupIds?: UUID[];
}

export interface AutoAssignRuleRequestCreateRequest extends AutoAssignRuleRequestBody {
    requestType: RuleRequestType;
    ruleId?: UUID;
}

/**
 * Request to update an auto-assign rule request.
 * Mirrors backend record AutoAssignRuleRequestUpdateRequest.
 */
export interface AutoAssignRuleRequestUpdateRequest extends AutoAssignRuleRequestBody {
    version: number;
}

/**
 * Request for approval/rejection with optional comment.
 */
export interface RuleRequestReviewRequest {
    comment?: string;
}

/**
 * Request to validate a condition expression.
 */
export interface ValidateExpressionRequest {
    expression: string;
    rootType?: string;
}

/**
 * Response from expression validation.
 */
export interface ValidateExpression {
    valid: boolean;
    errorMessage: string | null;
    errorPosition: number;
}

/**
 * Request to test a rule expression.
 * Mirrors backend record RuleTestRequest.
 */
export interface RuleTestRequest {
    conditionExpr: string;
    profileIds?: UUID[];
    limit?: number;
    ruleId?: UUID;
    rootType?: string;
    rootEntityId?: UUID;
}

export const TestResultAction = {
    ADD: 'ADD',
    REMOVE: 'REMOVE',
    UNCHANGED: 'UNCHANGED',
} as const;
export type TestResultAction = typeof TestResultAction[keyof typeof TestResultAction];

export interface TestResultItem {
    identityId: UUID;
    identityDisplayName: string;
    partyId: UUID;
    partyDisplayName: string;
    action: TestResultAction;
    profileIds: UUID[];
}

/**
 * Response from rule test execution.
 */
export interface RuleTest {
    totalEvaluated: number;
    matched: number;
    toAdd: number;
    toRemove: number;
    unchanged: number;
    items: TestResultItem[];
    testId: string;
}

/**
 * Paginated response for rule requests.
 */
export interface RuleRequestPage {
    content: RuleRequest[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}
